import { Form, Row } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo, useState } from "react";
import { DropdownSelect } from "components/DropdownSelect";
import { CustomInput } from "components/CustomInput";
import { CustomInputModal } from "components/CustomInputModal";
import { CustomDatePicker } from "components/DatePicker";
import { MaisFiltros } from "components/ButtonModal";
import { Multiselect } from "components/Multiselect";
import { useEmpresas } from "hooks/useEmpresas";
import { useMaskInput } from "hooks/useMaskInput";
import { useFornecedores } from "features/core/pages/Financeiro/Pages/Fornecedor/hooks/useFornecedores";
import { useGerenciarPagamentos } from "../../../../../hooks/useGerenciarPagamentos";
import { IFiltrosGerenciarPagamento } from "../../../../../interface/IFiltrosGerenciarPagamento";
import { FiltrosAprovadosSchema } from "../../../../../validate/filtrosAprovadosSchema";
import { ordenarBeneficiariosComTaxa } from "../../../../../constants/fornecedorTaxa";
import {
  Button,
  ButtonsContainer,
  DatePickerContainer,
  FormContainer,
  InputContainer,
  InputContainerMaisFiltros,
  MaisFiltrosContainer,
  MaisFiltrosDropdownWrapper,
} from "./style";

interface IProps {
  submitForm: (dados: IFiltrosGerenciarPagamento) => void;
}

const OPCOES_STATUS = [
  { value: "PENDENTE", label: "Pendente" },
  { value: "VALIDADO", label: "Validado" },
  { value: "PAGO", label: "Pago" },
];

export function FiltrosAprovados({ submitForm }: IProps) {
  const { handleSubmit, control, setValue } = useForm({
    resolver: yupResolver(FiltrosAprovadosSchema),
  });

  const [inicioAprovacao, setInicioAprovacao] = useState<Date>();
  const [fimAprovacao, setFimAprovacao] = useState<Date>();
  const [inicioPagamento, setInicioPagamento] = useState<Date>();
  const [fimPagamento, setFimPagamento] = useState<Date>();

  const {
    validarLotes,
    validarProcessos,
    validarNfRecibos,
    buscarUsuariosResponsaveis,
    buscarCustos,
  } = useGerenciarPagamentos();

  const [usuariosOptions, setUsuariosOptions] = useState<{ value: number; label: string }[]>([]);
  const [custosOptions, setCustosOptions] = useState<{ value: number; label: string }[]>([]);

  const { empresasOptions } = useEmpresas();
  const { mInputPlaca } = useMaskInput();
  const { optionsFornecedores, gerarOptionsFornecedores } = useFornecedores({ semFiltroStatus: true });

  useEffect(() => {
    gerarOptionsFornecedores();
    buscarCustos().then(setCustosOptions);
    buscarUsuariosResponsaveis().then(setUsuariosOptions);
  }, []);

  const beneficiariosOptions = ordenarBeneficiariosComTaxa(
    optionsFornecedores.map((opt) => ({ value: opt.value, label: opt.label })),
  ).map((opt) => ({ ...opt, checked: false }));

  const custosMultiselectOptions = useMemo(
    () =>
      custosOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
        checked: false,
      })),
    [custosOptions],
  );

  const onClickLimpar = () => {
    setValue("idsLotes", null);
    setValue("idsNfRecibo", null);
    setValue("idsProcessos", null);
    setValue("idsBeneficiarios", []);
    setValue("statusPagamento", null);
    setValue("idEmpresa", null);
    setValue("placa", "");
    setValue("idsCustos", []);
    setValue("idUsuario", null);
    setValue("periodoAprovacao", [null, null]);
    setValue("periodoPagamento", [null, null]);
    setInicioAprovacao(undefined);
    setFimAprovacao(undefined);
    setInicioPagamento(undefined);
    setFimPagamento(undefined);
    submitForm({});
  };

  const cancelarMaisFiltros = () => {
    setValue("idsNfRecibo", []);
    setValue("idEmpresa", null);
    setValue("placa", "");
    setValue("idsCustos", []);
    setValue("idUsuario", null);
    setValue("periodoAprovacao", [null, null]);
    setInicioAprovacao(undefined);
    setFimAprovacao(undefined);
  };

  return (
    <FormContainer>
      <Form
        noValidate
        onSubmit={handleSubmit((dados: any) => submitForm(dados as IFiltrosGerenciarPagamento))}
      >
        <Row className="mb-4">
          <InputContainer>
            <Form.Group>
              <Form.Label>ID Lote</Form.Label>
              <Controller
                control={control}
                name="idsLotes"
                defaultValue={[]}
                render={({ field: { onChange, value } }) => (
                  <CustomInputModal
                    label="Filtrar em massa"
                    value={value ?? []}
                    placeholder="Busque pelo lote"
                    onConfirm={onChange}
                    onClickOk={validarLotes}
                    validar={true}
                    style={{ width: "220px" }}
                    placeholderModal="Digite ou copie e cole os IDs dos lotes que deseja buscar e tecle ENTER"
                    labelItem="ID Lote"
                  />
                )}
              />
            </Form.Group>
          </InputContainer>

          <InputContainer>
            <Form.Group>
              <Form.Label>ID Processo</Form.Label>
              <Controller
                control={control}
                name="idsProcessos"
                defaultValue={[]}
                render={({ field: { onChange, value } }) => (
                  <CustomInputModal
                    label="Filtrar em massa"
                    value={value ?? []}
                    placeholder="Busque por ID"
                    onConfirm={onChange}
                    onClickOk={validarProcessos}
                    validar={true}
                    style={{ width: "220px" }}
                    placeholderModal="Digite ou copie e cole os IDs dos processos que deseja buscar e tecle ENTER"
                    labelItem="ID Processo"
                  />
                )}
              />
            </Form.Group>
          </InputContainer>

          <InputContainer>
            <Form.Group>
              <Controller
                control={control}
                name="periodoPagamento"
                render={({ field: { onChange } }) => (
                  <DatePickerContainer>
                    <span>Período de pagamento</span>
                    <CustomDatePicker
                      selected={inicioPagamento}
                      onChange={(update) => {
                        onChange(update);
                        const [start, end] = update as [Date, Date];
                        setInicioPagamento(start);
                        setFimPagamento(end);
                      }}
                      startDate={inicioPagamento}
                      endDate={fimPagamento}
                      rangeSelect={true}
                    />
                  </DatePickerContainer>
                )}
              />
            </Form.Group>
          </InputContainer>

          <InputContainer>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Controller
                control={control}
                name="statusPagamento"
                render={({ field: { onChange, value } }) => (
                  <DropdownSelect
                    options={OPCOES_STATUS}
                    title=""
                    selectedValue={value}
                    onSelect={onChange}
                    required
                  />
                )}
              />
            </Form.Group>
          </InputContainer>

          <InputContainer>
            <Form.Group>
              <Controller
                control={control}
                name="idsBeneficiarios"
                defaultValue={[]}
                render={({ field: { onChange, value } }) => (
                  <Multiselect
                    title="Beneficiário"
                    searchable
                    searchPlaceholder="Pesquisar"
                    selectAll
                    countPlaceholder
                    options={beneficiariosOptions}
                    onChange={onChange}
                    value={(value ?? []).filter((v): v is number => v !== undefined && v !== null)}
                    limitPlaceholder={25}
                  />
                )}
              />
            </Form.Group>
          </InputContainer>

          <InputContainer>
            <Form.Group>
              <ButtonsContainer>
                <Button
                  type="reset"
                  outline
                  variante="primary"
                  style={{ marginRight: "8px", width: "76px" }}
                  onClick={onClickLimpar}
                >
                  Limpar
                </Button>
                <Button type="submit" style={{ width: "76px" }}>
                  Buscar
                </Button>
              </ButtonsContainer>
            </Form.Group>
          </InputContainer>

          <InputContainer style={{ paddingTop: "5px" }}>
            <MaisFiltrosContainer>
              <Form.Group>
                <MaisFiltrosDropdownWrapper>
                  <MaisFiltros
                    limparTudoOnClick={cancelarMaisFiltros}
                    cancelarOnClick={cancelarMaisFiltros}
                    widthModal="lg"
                    showMaisFiltrosDefault={false}
                    handleClickAplicarFiltros={() => {}}
                    submit
                  >
                    <InputContainerMaisFiltros>
                      <Form.Group>
                        <Form.Label>Empresa</Form.Label>
                        <Controller
                          control={control}
                          name="idEmpresa"
                          render={({ field: { onChange, value } }) => (
                            <DropdownSelect
                              options={empresasOptions}
                              title=""
                              selectedValue={value}
                              onSelect={onChange}
                              searchable
                              required
                            />
                          )}
                        />
                      </Form.Group>
                    </InputContainerMaisFiltros>

                    <InputContainerMaisFiltros>
                      <Form.Group>
                        <Form.Label>Placa</Form.Label>
                        <Controller
                          control={control}
                          name="placa"
                          render={({ field: { onChange, value } }) => (
                            <CustomInput
                              type="text"
                              placeholder="Busque pela placa"
                              style={{ height: "2.2rem" }}
                              onChange={(e) => {
                                mInputPlaca(e);
                                onChange(e);
                              }}
                              value={value ?? undefined}
                            />
                          )}
                        />
                      </Form.Group>
                    </InputContainerMaisFiltros>

                    <InputContainerMaisFiltros style={{ height: "auto" }}>
                      <Form.Group>
                        <Controller
                          control={control}
                          name="idsCustos"
                          defaultValue={[]}
                          render={({ field: { onChange, value } }) => (
                            <Multiselect
                              title="Custos"
                              searchable
                              countPlaceholder
                              searchPlaceholder="Pesquisar custo"
                              options={custosMultiselectOptions}
                              onChange={onChange}
                              value={(value ?? []).filter((v): v is number => v !== undefined && v !== null)}
                              limitPlaceholder={40}
                            />
                          )}
                        />
                      </Form.Group>
                    </InputContainerMaisFiltros>

                    <InputContainerMaisFiltros>
                      <Form.Group>
                        <Form.Label>Usuário responsável</Form.Label>
                        <Controller
                          control={control}
                          name="idUsuario"
                          render={({ field: { onChange, value } }) => (
                            <DropdownSelect
                              options={usuariosOptions}
                              title=""
                              selectedValue={value}
                              onSelect={onChange}
                              searchable
                            />
                          )}
                        />
                      </Form.Group>
                    </InputContainerMaisFiltros>

                    <InputContainerMaisFiltros style={{ height: "auto" }}>
                      <Form.Group>
                        <Controller
                          control={control}
                          name="periodoAprovacao"
                          render={({ field: { onChange } }) => (
                            <DatePickerContainer>
                              <span>Período de aprovação</span>
                              <CustomDatePicker
                                selected={inicioAprovacao}
                                onChange={(update) => {
                                  onChange(update);
                                  const [start, end] = update as [Date, Date];
                                  setInicioAprovacao(start);
                                  setFimAprovacao(end);
                                }}
                                startDate={inicioAprovacao}
                                endDate={fimAprovacao}
                                rangeSelect={true}
                              />
                            </DatePickerContainer>
                          )}
                        />
                      </Form.Group>
                    </InputContainerMaisFiltros>

                    <InputContainerMaisFiltros>
                      <Form.Group>
                        <Form.Label>Recibo / NF</Form.Label>
                        <Controller
                          control={control}
                          name="idsNfRecibo"
                          defaultValue={[]}
                          render={({ field: { onChange, value } }) => (
                            <CustomInputModal
                              label="Filtrar em massa"
                              value={value ?? []}
                              placeholder="Busque pelo nº"
                              onConfirm={onChange}
                              onClickOk={validarNfRecibos}
                              validar={true}
                              style={{ width: "100%" }}
                              placeholderModal="Digite ou copie e cole os Nºs de Recibo/NF que deseja buscar e tecle ENTER"
                              labelItem="Recibo / NF"
                            />
                          )}
                        />
                      </Form.Group>
                    </InputContainerMaisFiltros>
                  </MaisFiltros>
                </MaisFiltrosDropdownWrapper>
              </Form.Group>
            </MaisFiltrosContainer>
          </InputContainer>
        </Row>
      </Form>
    </FormContainer>
  );
}
